import { uuidV4 } from '@repo/common-utils';
import { useUserEventLogger } from '@repo/tracking-service';
import { useDialog } from '@repo/ui';
import cn from 'clsx';
import { useAtom, useAtomValue } from 'jotai';
import { useRouter } from 'next/router';
import { memo, useEffect, useState } from 'react';

import { SurveyTopNavigation } from '@/components/survey/common/TopNavigation';
import { CTA, EmployeeInput } from '@/components/survey/common/ui';
import common from '@/components/survey/common/ui/styles/common.module.scss';
import { toggleBirthdayDot } from '@/components/survey/common/utils';
import { surveyStatusAtom } from '@/lib/stores/survey/common';
import { userInputCurrentlyEmployeesAtom } from '@/lib/stores/survey/employment';

export const UserInputEmployeeFormContainer = memo(({ id }: { id: string }) => {
  const router = useRouter();
  const { showDialog } = useDialog();
  const { sendClickEvent } = useUserEventLogger();
  const surveyStatus = useAtomValue(surveyStatusAtom);
  const [userInputEmployees, setUserInputEmployees] = useAtom(userInputCurrentlyEmployeesAtom);
  const [formErrorMessage, setFormErrorMessage] = useState({
    name: '',
    birth: ''
  });
  const [formData, setFormData] = useState({
    name: '',
    birth: ''
  });

  const employeeIndex = userInputEmployees.findIndex((userInputEmployee) => userInputEmployee.id === id);
  const isEditMode = !!id && !!userInputEmployees[employeeIndex];

  const setFormError = (key: keyof typeof formErrorMessage, message: string) => {
    setFormErrorMessage((prev) => ({
      ...prev,
      [key]: message
    }));
  };
  const setFormValue = (key: keyof typeof formData, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [key]: value
    }));
  };

  useEffect(() => {
    if (isEditMode) {
      setFormData({
        name: userInputEmployees[employeeIndex]?.name ?? '',
        birth: userInputEmployees[employeeIndex]?.birthday ?? ''
      });
    }
  }, [employeeIndex, userInputEmployees, isEditMode]);

  return (
    <>
      <SurveyTopNavigation
        onBackClick={() => {
          sendClickEvent('survey_special-relationships-info_back');
          router.back();
        }}
        rightButton={
          isEditMode
            ? {
                label: '삭제',
                onClick: () => {
                  sendClickEvent('survey_special-relationships-info_delete');
                  showDialog({
                    title: '삭제할까요?',
                    description: '입력한 정보를 삭제할게요.',
                    buttons: {
                      secondary: {
                        text: '취소',
                        onClick: () => sendClickEvent('survey_special-relationships-info_delete-cancel')
                      },
                      primary: {
                        variant: 'filled-error',
                        text: '삭제하기',
                        onClick: () => {
                          sendClickEvent('survey_special-relationships-info_delete-ok');

                          const copyUserInputEmployees = userInputEmployees.slice();
                          copyUserInputEmployees.splice(employeeIndex, 1);
                          setUserInputEmployees(copyUserInputEmployees);
                          router.back();
                        }
                      }
                    }
                  });
                }
              }
            : undefined
        }
      />
      <main className={cn(common.main_layout, common.main_cta_space)}>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            sendClickEvent('survey_special-relationships-info_confirm');

            if (formErrorMessage.name || formErrorMessage.birth) return;

            const copyUserInputEmployees = userInputEmployees.slice();
            const inputEmployee = {
              name: formData.name,
              birthday: formData.birth
            };
            if (isEditMode) {
              copyUserInputEmployees.splice(employeeIndex, 1, { id, ...inputEmployee });
            } else {
              copyUserInputEmployees.push({ id: uuidV4(), ...inputEmployee });
            }
            setUserInputEmployees(copyUserInputEmployees);
            router.back();
          }}
        >
          <article>
            <h1 className={common.content_title}>
              {surveyStatus.taxpayerName}님과 특수관계인 직원의
              <br />
              이름과 생년월일을 입력해주세요
            </h1>

            <EmployeeInput
              label={'이름'}
              required={true}
              value={formData.name}
              error={formErrorMessage.name}
              onFocus={() => sendClickEvent('survey_special-relationships-info_input-name')}
              onBlur={(e) => {
                if (e.target.value === '') {
                  setFormError('name', '이름을 입력해주세요.');
                  return;
                }
                const regex = /[가-힣a-zA-Z\s]$/.test(e.target.value);
                if (!regex) {
                  setFormError('name', '올바른 이름을 입력해주세요.');
                  return;
                }
                // error reset
                setFormError('name', '');
              }}
              onChange={(e) => {
                setFormValue('name', e.target.value);
              }}
            />
            <EmployeeInput
              label={'생년월일'}
              required={false}
              value={toggleBirthdayDot(formData.birth)}
              placeholder={'YY.MM.DD'}
              inputMode={'numeric'}
              maxLength={6}
              error={formErrorMessage.birth}
              onFocus={() => sendClickEvent('survey_special-relationships-info_input-date')}
              onChange={(e) => {
                setFormValue('birth', e.target.value);
              }}
              onBlur={(e) => {
                const replaceValue = e.target.value.replace(/\./g, '');
                const onlyNumberRegex = /[0-9]{6}/.test(replaceValue);
                const birthDateRegex = /^[0-9]{2}(?:0[1-9]|1[0-2])(?:0[1-9]|[1,2][0-9]|3[0,1])$/.test(replaceValue);
                if (e.target.value && !onlyNumberRegex) {
                  setFormError('birth', '6자리 숫자로 입력해주세요. 예) 900101');
                  return;
                } else if (e.target.value && !birthDateRegex) {
                  setFormError('birth', '올바른 생년월일을 입력해주세요.');
                  return;
                }
                // error reset
                setFormError('birth', '');
              }}
            />
          </article>

          <CTA type={'submit'} disabled={!formData.name || !!formErrorMessage.name || !!formErrorMessage.birth}>
            입력 완료
          </CTA>
        </form>
      </main>
    </>
  );
});
